import { LoggerEntry, LoggerEvent, LoggerLevel, LoggerProvider } from './logger';
import { DebugLogCategory } from '../../model/types/debug-log-type';
import { ProviderUtilities } from '../provider-utilities';

export interface IDebugLogItem {
  seqNr: number;
  time: string;
  category: DebugLogCategory;
  isWarning: boolean;
  title: string;
  entry: LoggerEntry;
  isHttp: boolean;
  method?: string;
  url?: string;
  status?: number;
  durationMs?: number;
  request?: LoggerEntry;
  response?: LoggerEntry;
}

const ERROR_EVENTS = [
  LoggerEvent.CommunicationError,
  LoggerEvent.ConnectionError,
  LoggerEvent.UnhandledException,
  LoggerEvent.ExceptionMessage,
  LoggerEvent.ExceptionStack,
  LoggerEvent.GeneralException,
  LoggerEvent.PageException,
  LoggerEvent.ComponentException,
  LoggerEvent.ProviderException,
];

const NATIVE_TRACKING_MESSAGE = /^(Tracking (event|view)|Segment native tracking)/;
const REQUEST_METHOD = /^(GET|POST|PUT|DELETE|DOWNLOAD)\b/;
const DURATION = /\((\d+) ms\)/;
const STATUS = /"status"\s*:\s*(-?\d{1,3})\b/;
const MAX_TITLE = 160;

export class DebugLogViewMapper {
  static readonly MAX_PRETTY = 20000;

  static toItems(entries: LoggerEntry[]): IDebugLogItem[] {
    const present = entries.filter((entry) => !!entry);
    const responses = DebugLogViewMapper.indexResponses(present);
    return present.map((entry) => DebugLogViewMapper.toItem(entry, responses));
  }

  static indexResponses(entries: LoggerEntry[]): { [requestSeqNr: number]: LoggerEntry } {
    const index: { [requestSeqNr: number]: LoggerEntry } = {};
    entries.forEach((entry) => {
      const request = DebugLogViewMapper.requestOf(entry);
      if (!request || request === entry)
        return;
      const current = index[request.seqNr];
      if (!current || (current.event !== LoggerEvent.CommunicationReceive && entry.event === LoggerEvent.CommunicationReceive) ||
          (current.event === entry.event && entry.seqNr > current.seqNr))
        index[request.seqNr] = entry;
    });
    return index;
  }

  static toItem(entry: LoggerEntry, responses: { [requestSeqNr: number]: LoggerEntry }): IDebugLogItem {
    const item: IDebugLogItem = {
      seqNr: entry.seqNr,
      time: DebugLogViewMapper.formatTime(entry.date),
      category: DebugLogViewMapper.categorize(entry),
      isWarning: entry.level === LoggerLevel.Warning,
      title: '',
      entry,
      isHttp: false,
    };

    const request = DebugLogViewMapper.requestOf(entry);
    if (request) {
      item.isHttp = true;
      item.request = request;
      item.response = responses[request.seqNr] || null;
      const parsed = DebugLogViewMapper.parseRequest(request.message);
      item.method = parsed.method;
      item.url = parsed.url;
      if (item.response) {
        item.status = DebugLogViewMapper.parseStatus(item.response.details);
        item.durationMs = DebugLogViewMapper.parseDuration(item.response.message);
      }
      const call = parsed.method ? parsed.method + ' ' + DebugLogViewMapper.shortUrl(parsed.url) : DebugLogViewMapper.firstLine(request.message);
      const isExchange = entry === request || entry.event === LoggerEvent.CommunicationReceive;
      item.title = isExchange ? call : DebugLogViewMapper.firstLine(entry.message) + ' · ' + call;
      return item;
    }

    if (entry.event === LoggerEvent.CommunicationReceive) {
      item.status = DebugLogViewMapper.parseStatus(entry.details);
      item.durationMs = DebugLogViewMapper.parseDuration(entry.message);
    }
    item.title = item.category === DebugLogCategory.Event ? DebugLogViewMapper.eventTitle(entry) : DebugLogViewMapper.firstLine(entry.message);
    return item;
  }

  static categorize(entry: LoggerEntry): DebugLogCategory {
    if (LoggerProvider.isErrorLevel(entry.level) || ERROR_EVENTS.indexOf(entry.event) >= 0)
      return DebugLogCategory.Error;
    if (entry.event === LoggerEvent.CommunicationSend)
      return DebugLogCategory.Request;
    if (entry.event === LoggerEvent.CommunicationReceive)
      return DebugLogCategory.Response;
    if (entry.event === LoggerEvent.Tracking || (entry.event === LoggerEvent.Info && NATIVE_TRACKING_MESSAGE.test(entry.message || '')))
      return DebugLogCategory.Event;
    if (entry.event === LoggerEvent.NavigationEnter || entry.event === LoggerEvent.NavigationLeave)
      return DebugLogCategory.Navigation;
    return DebugLogCategory.Other;
  }

  static requestOf(entry: LoggerEntry): LoggerEntry {
    if (entry.event === LoggerEvent.CommunicationSend)
      return entry;
    const parent = entry.debugParent || entry.parent;
    if (parent && parent.event === LoggerEvent.CommunicationSend &&
        (entry.event === LoggerEvent.CommunicationReceive || entry.event === LoggerEvent.CommunicationError || entry.event === LoggerEvent.ConnectionError))
      return parent;
    return null;
  }

  static parseRequest(message: string): { method: string, url: string } {
    const text = (message || '').trim();
    const method = REQUEST_METHOD.exec(text);
    if (!method)
      return { method: null, url: null };
    const parts = text.split(/\s+/);
    return { method: method[1], url: parts[parts.length - 1] };
  }

  static parseStatus(details: string): number {
    const match = STATUS.exec(details || '');
    return match ? parseInt(match[1], 10) : null;
  }

  static parseDuration(message: string): number {
    const match = DURATION.exec(message || '');
    return match ? parseInt(match[1], 10) : null;
  }

  static shortUrl(url: string): string {
    if (!url)
      return '';
    const withoutOrigin = url.replace(/^[a-z]+:\/\/[^/]+/i, '');
    return withoutOrigin || url;
  }

  static eventTitle(entry: LoggerEntry): string {
    const data = DebugLogViewMapper.parseJson(entry.details);
    const name = data && (data.eventName || data.eventname || data.event);
    const prefix = /view/i.test(entry.message || '') ? 'View' : /identify/i.test(entry.message || '') ? 'Identify' : 'Event';
    if (name && typeof name === 'string')
      return prefix + ': ' + name;
    return DebugLogViewMapper.firstLine(entry.message);
  }

  static firstLine(text: string): string {
    if (!text)
      return '(no message)';
    const line = text.split('\n')[0];
    return line.length > MAX_TITLE ? line.substr(0, MAX_TITLE) + '…' : line;
  }

  static formatTime(date: Date): string {
    if (!date)
      return '';
    const pad = ProviderUtilities.preFill;
    return pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds()) + '.' + pad(date.getMilliseconds(), 3);
  }

  static parseJson(text: string): any {
    if (!text)
      return null;
    try {
      return JSON.parse(text);
    } catch (ex) {
      return null;
    }
  }

  static pretty(text: string, maxLength = DebugLogViewMapper.MAX_PRETTY): string {
    if (!text)
      return '';
    const parsed = DebugLogViewMapper.parseJson(text);
    const out = parsed !== null && typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : text;
    return out.length > maxLength ? out.substr(0, maxLength) + '\n… (' + (out.length - maxLength) + ' more characters, use Copy for the full text)' : out;
  }

  static responseHeaders(response: LoggerEntry): { [name: string]: string } {
    if (!response)
      return null;
    if (response.debugHeaders)
      return response.debugHeaders;
    const parsed = DebugLogViewMapper.parseJson(response.details);
    const headers = parsed && parsed.headers;
    if (!headers || typeof headers !== 'object' || headers.normalizedNames)
      return null;
    return Object.keys(headers).length ? LoggerProvider.maskDebugHeaders(headers) : null;
  }

  static formatHeaders(headers: { [name: string]: string }): string {
    if (!headers)
      return '';
    return Object.keys(headers).map((name) => name + ': ' + headers[name]).join('\n');
  }

  static searchText(item: IDebugLogItem): string {
    return [item.title, item.entry.message, item.entry.details, item.url].filter((text) => !!text).join('\n').toLowerCase();
  }

  static matches(item: IDebugLogItem, categories: DebugLogCategory[], query: string, searchText: string = null): boolean {
    if (categories.length && categories.indexOf(item.category) < 0)
      return false;
    if (!query)
      return true;
    const text = searchText === null ? DebugLogViewMapper.searchText(item) : searchText;
    return text.indexOf(query.toLowerCase()) >= 0;
  }

  static urlWithParams(url: string, details: string): string {
    const params = DebugLogViewMapper.parseJson(details);
    if (!params || typeof params !== 'object' || url.indexOf('?') >= 0)
      return url;
    const query = Object.keys(params)
      .filter((key) => params[key] !== null && params[key] !== undefined && typeof params[key] !== 'object')
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(String(params[key])))
      .join('&');
    return query ? url + '?' + query : url;
  }

  static toCurl(item: IDebugLogItem): string {
    if (!item.isHttp || !item.method || !item.url)
      return null;
    const quote = (s: string) => '\'' + s.replace(/'/g, '\'\\\'\'') + '\'';
    const method = item.method === 'DOWNLOAD' ? 'GET' : item.method;
    let curl = 'curl -X ' + method + ' ' + quote(DebugLogViewMapper.urlWithParams(item.url, method === 'POST' || method === 'PUT' ? null : item.request.details));
    const headers = item.request.debugHeaders || {};
    Object.keys(headers).forEach((name) => {
      curl += ' \\\n  -H ' + quote(name + ': ' + headers[name]);
    });
    if (item.request.details && (method === 'POST' || method === 'PUT'))
      curl += ' \\\n  --data ' + quote(item.request.details);
    return curl;
  }
}



// WEBPACK FOOTER //
// ./src/providers/logger/debug-log-view.ts